import { useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { toast } from 'react-toastify'

import Button from '@material-ui/core/Button'
import InputAdornment from '@material-ui/core/InputAdornment'
import Paper from '@material-ui/core/Paper'
import TextField from '@material-ui/core/TextField'
import SearchIcon from '@material-ui/icons/Search'

import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'

import { isArray } from 'lodash'
import { Megaphone } from 'lucide-react'
import AnnouncementModal from '../../components/AnnouncementModal'
import ConfirmationModal from '../../components/ConfirmationModal'
import QuickTable from '../../components/QuickTable'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './style'

const reducer = (state, action) => {
  if (action.type === 'LOAD_ANNOUNCEMENTS') {
    const announcements = action.payload
    const newAnnouncements = []

    if (isArray(announcements)) {
      announcements.forEach(announcement => {
        const announcementIndex = state.findIndex(u => u.id === announcement.id)
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement
        } else {
          newAnnouncements.push(announcement)
        }
      })
    }

    return [...state, ...newAnnouncements]
  }

  if (action.type === 'UPDATE_ANNOUNCEMENTS') {
    const announcement = action.payload
    const announcementIndex = state.findIndex(u => u.id === announcement.id)

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement
      return [...state]
    } else {
      return [announcement, ...state]
    }
  }

  if (action.type === 'DELETE_ANNOUNCEMENT') {
    const announcementId = action.payload

    const announcementIndex = state.findIndex(u => u.id === announcementId)
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Announcements = () => {
  const classes = useStyles()

  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null)
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null)
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [announcements, dispatch] = useReducer(reducer, [])

  const socketManager = useContext(SocketContext)

  const columns = useMemo(
    () => [
      {
        field: 'title',
        headerName: i18n.t('announcements.table.title'),
        align: 'center'
      },
      {
        field: 'priority',
        headerName: i18n.t('announcements.table.priority'),
        align: 'center'
      },
      {
        field: 'isConnectionAnnouncement',
        headerName: 'Anúncio de Conexão',
        align: 'center'
      },
      {
        field: 'status',
        headerName: i18n.t('announcements.table.status'),
        align: 'center'
      },
      { field: 'linkout', headerName: 'Link', align: 'center' }
    ],
    []
  )

  const customCellRenderer = {
    priority: item => {
      const priorities = { 1: 'Alta', 2: 'Média', 3: 'Baixa' }
      return priorities[item.priority] || item.priority
    },
    isConnectionAnnouncement: item =>
      item.isConnectionAnnouncement ? 'Sim' : 'Não',
    status: item => (item.status ? 'Ativo' : 'Inativo')
  }

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [searchParam])

  useEffect(() => {
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const onAnnouncement = data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_ANNOUNCEMENTS', payload: data.record })
      }
      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_ANNOUNCEMENT', payload: +data.id })
      }
    }

    socket.on(`company-announcement`, onAnnouncement)

    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get('/announcements/', {
        params: { searchParam, pageNumber }
      })
      dispatch({ type: 'LOAD_ANNOUNCEMENTS', payload: data.records })
      setHasMore(data.hasMore)
      setLoading(false)
    } catch (err) {
      toastError(err)
    }
  }

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null)
    setAnnouncementModalOpen(true)
  }

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null)
    setAnnouncementModalOpen(false)
  }

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase())
  }

  const handleDeleteAnnouncement = async announcementId => {
    try {
      await api.delete(`/announcements/${announcementId}`)
      toast.success(i18n.t('announcements.toasts.deleted'))
    } catch (err) {
      toastError(err)
    }
    setDeletingAnnouncement(null)
    setSearchParam('')
    setPageNumber(1)
  }

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1)
  }

  const handleScroll = e => {
    if (!hasMore || loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  const handleEdit = announcement => {
    setSelectedAnnouncement(announcement)
    setAnnouncementModalOpen(true)
  }

  const handleDeleteRequest = announcement => {
    setDeletingAnnouncement(announcement)
    setConfirmModalOpen(true)
  }

  return (
    <MainContainer>
      <ConfirmationModal
        title="Excluir Informativo?"
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement.id)}
      >
        {i18n.t('announcements.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <AnnouncementModal
        resetPagination={() => {
          setPageNumber(1)
          fetchAnnouncements()
        }}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />

      <MainHeader
        title={i18n.t('announcements.title')}
        description="Crie e gerencie os informativos do sistema."
        showTutorial
      // onTutorialClick={handleOpenTutorial}
      >
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t('contacts.searchPlaceholder')}
            variant="outlined"
            type="search"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            // color="primary"
            onClick={handleOpenAnnouncementModal}
            className={classes.buttonNewAnnouncement}
            startIcon={<Megaphone />}
          >
            {/* {i18n.t('quickMessages.buttons.add')} */}
            Novo Informativo
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <QuickTable
          data={announcements}
          columns={columns}
          loading={loading}
          actions={['edit', 'delete']}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          customCellRenderer={customCellRenderer}
          containerProps={{
            className: classes.mainPaper,
            onScroll: handleScroll
          }}
          emptyMessage="Nenhum informativo encontrado"
        />
      </Paper>
    </MainContainer>
  )
}

export default Announcements
